import React, { useEffect, useMemo, useState } from "react";
import { useNotification } from "../../../context/NotificationContext";
import {
  deleteAdminSolution,
  getAdminSolutionById,
  getAdminSolutions,
  saveAdminSolutionAllLanguages,
  uploadAdminSolutionTabImage,
} from "../../../api/solutionsAdmin";
import { resolveMediaUrl } from "../../../utils/media";
import AdminProductPdfsPanel from "./AdminProductPdfsPanel";
import AdminEditorModal from "./AdminEditorModal";
import AdminCollapsibleSection from "./AdminCollapsibleSection";
import AdminProductListCard from "./AdminProductListCard";
import { DashboardReloadButton } from "./dashboardUi";
import { btnAccent, btnDanger, btnPrimary, btnSecondary, fileInputClass, langTabActive, langTabIdle } from "./adminProductUi";

const LANGS = ["en", "ar", "ku"];
const LANG_LABELS = { en: "English", ar: "Arabic", ku: "Kurdish" };

const normLang = (value) =>
  String(value || "en")
    .toLowerCase()
    .split("-")[0];

const normKey = (value) => String(value || "").trim().toLowerCase();

const emptyTab = () => ({ id: null, label: "", tabImageUrls: [], descriptionBody: "", features: [] });
const emptyProduct = () => ({ title: "", description: "", imageUrl: "" });
const emptyFeature = () => ({ title: "", description: "" });

/** All translatable content for a single language variant of a solution. */
const emptyLangFields = () => ({
  heroTag: "",
  title: "",
  introDescription: "",
  cardDescription: "",
  heroDescription: "",
  specificationsLabel: "",
  descriptionHeading: "",
  descriptionBody: "",
  productsTag: "",
  productsTitle: "",
  productsTitleAccent: "",
  featuresTag: "",
  featuresTitle: "",
  featuresTitleAccent: "",
  descriptionTabs: [emptyTab()],
  products: [emptyProduct()],
  features: [emptyFeature()],
});

const emptyTranslations = () => ({
  en: emptyLangFields(),
  ar: emptyLangFields(),
  ku: emptyLangFields(),
});

const EMPTY_FORM = {
  solutionKey: "",
  cardIconUrl: "",
  heroImageUrl: "",
  isActive: true,
  translations: emptyTranslations(),
  ids: {},
  primaryId: null,
};

const parseTabImageUrls = (tab) => {
  const arraySource = tab?.tabImageUrls ?? tab?.TabImageUrls ?? tab?.images ?? tab?.Images;
  const fromArray = Array.isArray(arraySource)
    ? arraySource.map((v) => String(v || "").trim()).filter(Boolean)
    : [];
  const pipeSource = tab?.tabImageUrl ?? tab?.TabImageUrl ?? "";
  const fromPipe = String(pipeSource)
    .split("|")
    .map((v) => v.trim())
    .filter(Boolean);
  const merged = [];
  [...fromArray, ...fromPipe].forEach((url) => {
    if (!merged.includes(url)) merged.push(url);
  });
  return merged;
};

const getHeroImageCandidate = (solution) =>
  solution?.heroImageUrl || solution?.heroImage || solution?.imageUrl || solution?.image || "";

const getCardIconCandidate = (solution) =>
  solution?.cardIconUrl || solution?.cardIcon || solution?.iconUrl || solution?.icon || "";

/** Map a single DB solution row into the per-language form field shape. */
const mapRowToLangFields = (row) => ({
  heroTag: row?.heroTag || "",
  title: row?.title || "",
  introDescription: row?.introDescription || "",
  cardDescription: row?.cardDescription || "",
  heroDescription: row?.heroDescription || "",
  specificationsLabel: row?.specificationsLabel || "",
  descriptionHeading: row?.descriptionHeading || "",
  descriptionBody: row?.descriptionBody || "",
  productsTag: row?.productsTag || "",
  productsTitle: row?.productsTitle || "",
  productsTitleAccent: row?.productsTitleAccent || "",
  featuresTag: row?.featuresTag || "",
  featuresTitle: row?.featuresTitle || "",
  featuresTitleAccent: row?.featuresTitleAccent || "",
  descriptionTabs: Array.isArray(row?.descriptionTabs) && row.descriptionTabs.length
    ? row.descriptionTabs.map((it) => ({
        id: it?.id ?? it?.Id ?? null,
        label: it?.label || it?.Label || "",
        tabImageUrls: parseTabImageUrls(it),
        descriptionBody: it?.descriptionBody || it?.DescriptionBody || "",
        features: Array.isArray(it?.features)
          ? it.features.map((f) => ({ title: f?.title || "", description: f?.description || "" }))
          : [],
      }))
    : [emptyTab()],
  products: Array.isArray(row?.products) && row.products.length
    ? row.products.map((it) => ({
        title: it?.title || "",
        description: it?.description || "",
        imageUrl: it?.imageUrl || "",
      }))
    : [emptyProduct()],
  features: Array.isArray(row?.features) && row.features.length
    ? row.features.map((it) => ({ title: it?.title || "", description: it?.description || "" }))
    : [emptyFeature()],
});

/** Build one form (all languages) from the sibling rows of one solution key. */
const mapSolutionsToForm = (rows) => {
  const list = Array.isArray(rows) ? rows.filter(Boolean) : [];
  const primary = list.find((r) => normLang(r?.language) === "en") || list[0] || {};
  const translations = emptyTranslations();
  const ids = {};
  list.forEach((row) => {
    const lang = normLang(row?.language);
    if (!LANGS.includes(lang)) return;
    translations[lang] = mapRowToLangFields(row);
    ids[lang] = row?.id ?? null;
  });
  return {
    solutionKey: primary?.solutionKey || "",
    cardIconUrl: getCardIconCandidate(primary) || getCardIconCandidate(list.find((r) => getCardIconCandidate(r))) || "",
    heroImageUrl: getHeroImageCandidate(primary) || getHeroImageCandidate(list.find((r) => getHeroImageCandidate(r))) || "",
    isActive: typeof primary?.isActive === "boolean" ? primary.isActive : true,
    translations,
    ids,
    primaryId: primary?.id ?? list[0]?.id ?? null,
  };
};

const langHasContent = (f) => Boolean((f?.title || "").trim());

/** Convert one language's form fields into a backend language-content payload. */
const toLanguageContent = (lang, fields, englishFields = null) => {
  const enTabs = Array.isArray(englishFields?.descriptionTabs) ? englishFields.descriptionTabs : [];
  const enFeatures = Array.isArray(englishFields?.features) ? englishFields.features : [];
  const enProducts = Array.isArray(englishFields?.products) ? englishFields.products : [];

  let descriptionTabs = (fields.descriptionTabs || [])
    .map((it, idx) => {
      const enTab = enTabs[idx];
      const features = (it?.features || [])
        .map((f) => ({ title: String(f?.title || "").trim(), description: String(f?.description || "").trim() }))
        .filter((f) => f.title);
      const fallbackFeatures =
        lang !== "en" && !features.length && Array.isArray(enTab?.features)
          ? enTab.features
              .map((f) => ({
                title: String(f?.title || "").trim(),
                description: String(f?.description || "").trim(),
              }))
              .filter((f) => f.title)
          : [];

      return {
        label: String(it?.label || "").trim(),
        tabImageUrls: (it?.tabImageUrls || []).map((v) => String(v || "").trim()).filter(Boolean),
        descriptionBody:
          String(it?.descriptionBody || "").trim() ||
          (lang !== "en" ? String(enTab?.descriptionBody || "").trim() : "") ||
          null,
        features: features.length ? features : fallbackFeatures,
      };
    })
    .filter((it) => it.label);

  if (lang !== "en" && !descriptionTabs.length && enTabs.length) {
    descriptionTabs = enTabs
      .map((it) => ({
        label: String(it?.label || "").trim(),
        tabImageUrls: (it?.tabImageUrls || []).map((v) => String(v || "").trim()).filter(Boolean),
        descriptionBody: String(it?.descriptionBody || "").trim() || null,
        features: (it?.features || [])
          .map((f) => ({ title: String(f?.title || "").trim(), description: String(f?.description || "").trim() }))
          .filter((f) => f.title),
      }))
      .filter((it) => it.label);
  }

  let features = (fields.features || [])
    .map((it) => ({ title: String(it?.title || "").trim(), description: String(it?.description || "").trim() }))
    .filter((it) => it.title);
  if (lang !== "en" && !features.length && enFeatures.length) {
    features = enFeatures
      .map((it) => ({ title: String(it?.title || "").trim(), description: String(it?.description || "").trim() }))
      .filter((it) => it.title);
  }

  let products = (fields.products || [])
    .map((it) => ({
      title: String(it?.title || "").trim(),
      description: String(it?.description || "").trim(),
      imageUrl: String(it?.imageUrl || "").trim() || null,
    }))
    .filter((it) => it.title);
  if (lang !== "en" && !products.length && enProducts.length) {
    products = enProducts
      .map((it) => ({
        title: String(it?.title || "").trim(),
        description: String(it?.description || "").trim(),
        imageUrl: String(it?.imageUrl || "").trim() || null,
      }))
      .filter((it) => it.title);
  }

  return {
    language: lang,
    heroTag: (fields.heroTag || "").trim(),
    title: (fields.title || "").trim(),
    introDescription: (fields.introDescription || "").trim(),
    cardDescription: (fields.cardDescription || "").trim(),
    heroDescription: (fields.heroDescription || "").trim(),
    specificationsLabel: (fields.specificationsLabel || "").trim(),
    descriptionHeading: (fields.descriptionHeading || "").trim(),
    descriptionBody: (fields.descriptionBody || "").trim(),
    productsTag: (fields.productsTag || "").trim(),
    productsTitle: (fields.productsTitle || "").trim(),
    productsTitleAccent: (fields.productsTitleAccent || "").trim(),
    featuresTag: (fields.featuresTag || "").trim(),
    featuresTitle: (fields.featuresTitle || "").trim(),
    featuresTitleAccent: (fields.featuresTitleAccent || "").trim(),
    descriptionTabs,
    products,
    features,
  };
};

/** Build the multi-language save payload. Incomplete languages are omitted. */
const buildSavePayload = (form) => {
  const englishFields = form.translations?.en || null;
  return {
    solutionKey: (form.solutionKey || "").trim(),
    cardIconUrl: (form.cardIconUrl || "").trim() || null,
    heroImageUrl: (form.heroImageUrl || "").trim() || null,
    isActive: Boolean(form.isActive),
    languages: LANGS.filter((lang) => langHasContent(form.translations?.[lang])).map((lang) =>
      toLanguageContent(lang, form.translations[lang], englishFields)
    ),
  };
};

const SectionArrayEditor = ({ label, items, onChange, itemFactory, renderItem, addText }) => (
  <div className="rounded-xl border border-slate-200 p-3">
    <p className="text-sm font-semibold text-[#011337] mb-2">{label}</p>
    {items.map((item, idx) => (
      <div key={`${label}-${idx}`} className="mb-2">
        {renderItem(item, idx, (nextItem) => {
          onChange(items.map((it, i) => (i === idx ? nextItem : it)));
        })}
        <button
          type="button"
          onClick={() => onChange(items.filter((_, i) => i !== idx))}
          className="mt-1 text-xs px-2 py-1 rounded border border-rose-200 text-rose-700 hover:bg-rose-50"
        >
          Remove
        </button>
      </div>
    ))}
    <button
      type="button"
      onClick={() => onChange([...items, itemFactory()])}
      className="text-xs px-3 py-1.5 rounded border border-slate-300 hover:bg-slate-50"
    >
      {addText}
    </button>
  </div>
);

const SolutionsManager = () => {
  const { notify } = useNotification();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_FORM);
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [editingKey, setEditingKey] = useState(null);
  const [activeCreateLang, setActiveCreateLang] = useState("en");
  const [activeEditLang, setActiveEditLang] = useState("en");
  const [tabImageUploading, setTabImageUploading] = useState(null);
  const [tabImagePreviews, setTabImagePreviews] = useState({});

  const loadItems = async () => {
    setLoading(true);
    try {
      const data = await getAdminSolutions();
      setItems(Array.isArray(data) ? data : []);
    } catch (error) {
      notify({ type: "error", title: "Failed to load solutions", message: error?.response?.data?.message || error?.message || "" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
  }, []);

  // One card per solution key; each card bundles all language variants.
  const groupedItems = useMemo(() => {
    const groups = new Map();
    items.forEach((item) => {
      const key = normKey(item?.solutionKey) || `#${item?.id}`;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(item);
    });
    return [...groups.values()]
      .map((rows) => {
        const rep = rows.find((r) => normLang(r?.language) === "en") || rows[0];
        return {
          key: normKey(rep?.solutionKey) || `#${rep?.id}`,
          rep,
          rows,
          languages: LANGS.filter((lang) => rows.some((r) => normLang(r?.language) === lang)),
        };
      })
      .sort((a, b) => Number(a?.rep?.id || 0) - Number(b?.rep?.id || 0));
  }, [items]);

  const mergeFreshIntoItems = (fresh) =>
    setItems((prev) => prev.map((x) => fresh.find((f) => Number(f?.id) === Number(x.id)) || x));

  const refreshSolutionKey = async (solutionKey) => {
    await loadItems();
    if (solutionKey) {
      const siblings = (await getAdminSolutions().catch(() => [])).filter(
        (b) => normKey(b?.solutionKey) === normKey(solutionKey)
      );
      const fresh = await Promise.all(siblings.map((s) => getAdminSolutionById(s.id).catch(() => s)));
      mergeFreshIntoItems(fresh);
      setEditForm(mapSolutionsToForm(fresh));
    }
  };

  const handleCreate = async () => {
    const anyLang = LANGS.some((lang) => langHasContent(createForm.translations[lang]));
    if (!createForm.solutionKey.trim() || !anyLang) {
      notify({ type: "error", title: "Solution key and at least one language with a Title are required" });
      return;
    }
    setCreating(true);
    try {
      const payload = buildSavePayload(createForm);
      await saveAdminSolutionAllLanguages(payload);
      notify({ type: "success", title: "Solution saved for all languages" });
      setCreateForm(EMPTY_FORM);
      setActiveCreateLang("en");
      await loadItems();
    } catch (error) {
      notify({ type: "error", title: "Save failed", message: error?.response?.data?.message || error?.message || "" });
    } finally {
      setCreating(false);
    }
  };

  const startEdit = async (group) => {
    const key = normKey(group?.rep?.solutionKey) || group.key;
    setEditingKey(key);
    setActiveEditLang("en");
    setTabImagePreviews({});
    setEditForm(mapSolutionsToForm(group.rows));
    try {
      const fresh = await Promise.all(group.rows.map((row) => getAdminSolutionById(row.id).catch(() => row)));
      mergeFreshIntoItems(fresh);
      setEditForm(mapSolutionsToForm(fresh));
    } catch {
      /* keep list rows */
    }
  };

  const saveEdit = async () => {
    if (!editingKey) return;
    const anyLang = LANGS.some((lang) => langHasContent(editForm.translations[lang]));
    if (!editForm.solutionKey.trim() || !anyLang) {
      notify({ type: "error", title: "Solution key and at least one language with a Title are required" });
      return;
    }
    setSaving(true);
    try {
      const payload = buildSavePayload(editForm);
      await saveAdminSolutionAllLanguages(payload);
      notify({ type: "success", title: "Solution updated for all languages" });
      setEditingKey(null);
      setEditForm(EMPTY_FORM);
      setTabImagePreviews({});
      await loadItems();
    } catch (error) {
      notify({ type: "error", title: "Update failed", message: error?.response?.data?.message || error?.message || "" });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (group) => {
    if (!window.confirm(`Delete solution "${group?.rep?.title || group?.rep?.solutionKey}" and all its language versions?`)) return;
    try {
      for (const row of group.rows) {
        // eslint-disable-next-line no-await-in-loop
        await deleteAdminSolution({ id: row.id });
      }
      notify({ type: "success", title: "Solution deleted" });
      await loadItems();
    } catch (error) {
      notify({ type: "error", title: "Delete failed", message: error?.response?.data?.message || error?.message || "" });
    }
  };

  const closeEdit = () => {
    setEditingKey(null);
    setEditForm(EMPTY_FORM);
    setActiveEditLang("en");
    setTabImagePreviews({});
  };

  const editRepId = editForm.ids?.en ?? editForm.primaryId ?? null;

  const handleTabImageReplace = async (lang, tabIdx, imgIdx, file) => {
    if (!file) return;
    const rowId = editForm.ids?.[lang];
    const tab = editForm.translations?.[lang]?.descriptionTabs?.[tabIdx];
    if (!rowId || !tab?.id) {
      notify({ type: "error", title: "Save the solution first before replacing tab images." });
      return;
    }
    const previewKey = `${lang}-${tabIdx}-${imgIdx}`;
    const objectUrl = URL.createObjectURL(file);
    setTabImagePreviews((prev) => ({ ...prev, [previewKey]: objectUrl }));
    setTabImageUploading(previewKey);
    try {
      await uploadAdminSolutionTabImage({ solutionId: rowId, tabId: tab.id, file, imageIndex: imgIdx });
      notify({ type: "success", title: "Tab image updated" });
      setTabImagePreviews((prev) => {
        const next = { ...prev };
        delete next[previewKey];
        return next;
      });
      URL.revokeObjectURL(objectUrl);
      await refreshSolutionKey(editForm.solutionKey);
    } catch (error) {
      notify({ type: "error", title: "Image upload failed", message: error?.response?.data?.message || error?.message || "" });
    } finally {
      setTabImageUploading(null);
    }
  };

  const renderSharedFields = (form, setForm, { isEdit = false } = {}) => (
    <div className="grid sm:grid-cols-2 gap-3">
      <label className="text-sm text-slate-700">
        Solution Key (shared across languages)
        <input
          type="text"
          value={form.solutionKey}
          readOnly={isEdit}
          onChange={(e) => setForm((prev) => ({ ...prev, solutionKey: e.target.value }))}
          placeholder="exterior-system"
          className={`mt-1 w-full rounded-lg border px-3 py-2 text-sm ${
            isEdit ? "border-slate-200 bg-slate-100 text-slate-500" : "border-slate-300 bg-white"
          }`}
        />
        {isEdit ? <span className="mt-1 block text-xs text-slate-400">Shared and cannot be changed here.</span> : null}
      </label>
      <label className="inline-flex items-center gap-2 text-sm text-slate-700">
        <input
          type="checkbox"
          checked={Boolean(form.isActive)}
          onChange={(e) => setForm((prev) => ({ ...prev, isActive: e.target.checked }))}
        />
        Active
      </label>
      <label className="text-sm text-slate-700">
        Card Icon URL (shared)
        <input
          type="text"
          value={form.cardIconUrl}
          onChange={(e) => setForm((prev) => ({ ...prev, cardIconUrl: e.target.value }))}
          placeholder="/uploads/... or https://..."
          className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
        />
      </label>
      <label className="text-sm text-slate-700">
        Hero Image URL (shared)
        <input
          type="text"
          value={form.heroImageUrl}
          onChange={(e) => setForm((prev) => ({ ...prev, heroImageUrl: e.target.value }))}
          placeholder="/uploads/... or https://..."
          className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
        />
      </label>
    </div>
  );

  // Shared per-language content editor used by both create and edit forms.
  const renderLangContent = (form, setForm, lang, formId) => {
    const fields = form.translations[lang] || emptyLangFields();
    const dir = lang === "ar" || lang === "ku" ? "rtl" : "ltr";

    const patch = (partial) =>
      setForm((prev) => ({
        ...prev,
        translations: { ...prev.translations, [lang]: { ...prev.translations[lang], ...partial } },
      }));

    const setArray = (key, nextValue) =>
      setForm((prev) => ({
        ...prev,
        translations: { ...prev.translations, [lang]: { ...prev.translations[lang], [key]: nextValue } },
      }));

    const textField = (key, label, { textarea = false, colSpan = false } = {}) => (
      <label className={`text-sm text-slate-700 ${colSpan ? "sm:col-span-2" : ""}`}>
        {label} ({lang.toUpperCase()})
        {textarea ? (
          <textarea
            value={fields[key]}
            onChange={(e) => patch({ [key]: e.target.value })}
            className="mt-1 w-full min-h-[80px] rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
          />
        ) : (
          <input
            type="text"
            value={fields[key]}
            onChange={(e) => patch({ [key]: e.target.value })}
            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
          />
        )}
      </label>
    );

    return (
      <div className="grid gap-4" dir={dir}>
        <div className="grid sm:grid-cols-2 gap-3">
          {textField("heroTag", "Hero Tag")}
          {textField("title", "Title")}
          {textField("introDescription", "Intro Description", { textarea: true, colSpan: true })}
          {textField("cardDescription", "Card Description", { textarea: true, colSpan: true })}
          {textField("heroDescription", "Hero Description", { textarea: true, colSpan: true })}
          {textField("specificationsLabel", "Specifications Label")}
          {textField("descriptionHeading", "Description Heading")}
          {textField("descriptionBody", "Description Body", { textarea: true, colSpan: true })}
          {textField("productsTag", "Products Tag")}
          {textField("productsTitle", "Products Title")}
          {textField("productsTitleAccent", "Products Title Accent")}
          {textField("featuresTag", "Features Tag")}
          {textField("featuresTitle", "Features Title")}
          {textField("featuresTitleAccent", "Features Title Accent")}
        </div>

        <SectionArrayEditor
          label={`Description Tabs (${lang.toUpperCase()})`}
          items={fields.descriptionTabs}
          onChange={(next) => setArray("descriptionTabs", next.length ? next : [emptyTab()])}
          itemFactory={emptyTab}
          addText="Add tab"
          renderItem={(item, idx, onItemChange) => (
            <div className="rounded-lg border border-slate-200 bg-white p-3 space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Tab {idx + 1}</p>
              <label className="block text-sm text-slate-700">
                Tab label
                <input
                  type="text"
                  value={item.label}
                  onChange={(e) => onItemChange({ ...item, label: e.target.value })}
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  placeholder="Tab label"
                />
              </label>
              <label className="block text-sm text-slate-700">
                Tab description body
                <textarea
                  value={item.descriptionBody}
                  onChange={(e) => onItemChange({ ...item, descriptionBody: e.target.value })}
                  className="mt-1 w-full min-h-[60px] rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
              </label>

              <div className="rounded-lg border border-slate-100 bg-slate-50 p-2">
                <p className="text-xs font-semibold text-slate-600 mb-2">Tab features</p>
                {(item.features || []).map((feature, featureIdx) => (
                  <div key={`tab-feature-${idx}-${featureIdx}`} className="mb-2 grid sm:grid-cols-2 gap-2">
                    <input
                      type="text"
                      value={feature.title}
                      onChange={(e) => {
                        const nextFeatures = (item.features || []).map((f, i) =>
                          i === featureIdx ? { ...f, title: e.target.value } : f
                        );
                        onItemChange({ ...item, features: nextFeatures });
                      }}
                      className="rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
                      placeholder="Feature title"
                    />
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={feature.description}
                        onChange={(e) => {
                          const nextFeatures = (item.features || []).map((f, i) =>
                            i === featureIdx ? { ...f, description: e.target.value } : f
                          );
                          onItemChange({ ...item, features: nextFeatures });
                        }}
                        className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
                        placeholder="Feature description"
                      />
                      <button
                        type="button"
                        onClick={() =>
                          onItemChange({
                            ...item,
                            features: (item.features || []).filter((_, i) => i !== featureIdx),
                          })
                        }
                        className="text-xs px-2 py-1 rounded border border-rose-200 text-rose-700 hover:bg-rose-50"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() =>
                    onItemChange({
                      ...item,
                      features: [...(item.features || []), emptyFeature()],
                    })
                  }
                  className="text-xs px-3 py-1.5 rounded border border-slate-300 hover:bg-white"
                >
                  Add tab feature
                </button>
              </div>

              {formId === "edit" ? (
                <div>
                  <p className="text-sm font-medium text-slate-700 mb-2">Tab images</p>
                  {(item.tabImageUrls || []).length ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {(item.tabImageUrls || []).map((url, imgIdx) => {
                        const previewKey = `${lang}-${idx}-${imgIdx}`;
                        const src = tabImagePreviews[previewKey] || resolveMediaUrl(url) || url;
                        const uploading = tabImageUploading === previewKey;
                        return (
                          <div key={previewKey} className="rounded-lg border border-slate-200 bg-slate-50 p-2">
                            {src ? (
                              <img src={src} alt="" className="w-full h-28 object-cover rounded-md border border-slate-200 bg-white" />
                            ) : (
                              <div className="w-full h-28 rounded-md border border-dashed border-slate-300 bg-white" />
                            )}
                            <label className="mt-2 block">
                              <span className="text-xs font-medium text-[#0b1f3f]">
                                {uploading ? "Uploading..." : "Change image"}
                              </span>
                              <input
                                type="file"
                                accept="image/*"
                                disabled={uploading}
                                className={`mt-1 block w-full ${fileInputClass}`}
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) handleTabImageReplace(lang, idx, imgIdx, file);
                                  e.target.value = "";
                                }}
                              />
                            </label>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-xs text-slate-500">No images for this tab yet. Save the solution to enable uploads.</p>
                  )}
                </div>
              ) : null}
            </div>
          )}
        />

        <SectionArrayEditor
          label={`Products (${lang.toUpperCase()})`}
          items={fields.products}
          onChange={(next) => setArray("products", next.length ? next : [emptyProduct()])}
          itemFactory={emptyProduct}
          addText="Add product"
          renderItem={(item, _idx, onItemChange) => (
            <div className="grid sm:grid-cols-3 gap-2">
              <input
                type="text"
                value={item.title}
                onChange={(e) => onItemChange({ ...item, title: e.target.value })}
                className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                placeholder="Title"
              />
              <input
                type="text"
                value={item.description}
                onChange={(e) => onItemChange({ ...item, description: e.target.value })}
                className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                placeholder="Description"
              />
              <input
                type="text"
                value={item.imageUrl}
                onChange={(e) => onItemChange({ ...item, imageUrl: e.target.value })}
                className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                placeholder="/uploads/solutions/fire.png"
              />
            </div>
          )}
        />

        <SectionArrayEditor
          label={`Features (${lang.toUpperCase()})`}
          items={fields.features}
          onChange={(next) => setArray("features", next.length ? next : [emptyFeature()])}
          itemFactory={emptyFeature}
          addText="Add feature"
          renderItem={(item, _idx, onItemChange) => (
            <div className="grid sm:grid-cols-2 gap-2">
              <input
                type="text"
                value={item.title}
                onChange={(e) => onItemChange({ ...item, title: e.target.value })}
                className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                placeholder="Feature title"
              />
              <input
                type="text"
                value={item.description}
                onChange={(e) => onItemChange({ ...item, description: e.target.value })}
                className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                placeholder="Feature description"
              />
            </div>
          )}
        />
      </div>
    );
  };

  const renderLangTabs = (activeLang, setActiveLang, form, keyPrefix) => (
    <div className="flex flex-wrap items-center gap-2">
      {LANGS.map((lang) => {
        const hasContent = langHasContent(form.translations?.[lang]);
        return (
          <button
            key={`${keyPrefix}-${lang}`}
            type="button"
            onClick={() => setActiveLang(lang)}
            className={activeLang === lang ? langTabActive : langTabIdle}
          >
            {LANG_LABELS[lang]}
            {hasContent ? " ●" : ""}
          </button>
        );
      })}
    </div>
  );

  return (
    <section className="space-y-4">
      <div className="flex justify-end">
        <DashboardReloadButton onClick={loadItems} loading={loading} label="Reload list" />
      </div>

      <AdminCollapsibleSection
        title="Add new solution"
        description="Fill in every language (English, Arabic, Kurdish). All languages are saved together."
        badge="Create"
        footer={
          <button type="button" disabled={creating} onClick={handleCreate} className={btnAccent}>
            {creating ? "Saving..." : "Save solution (all languages)"}
          </button>
        }
      >
        <div className="grid gap-4">
          {renderSharedFields(createForm, setCreateForm)}
          {renderLangTabs(activeCreateLang, setActiveCreateLang, createForm, "create")}
          {renderLangContent(createForm, setCreateForm, activeCreateLang, "create")}
        </div>
      </AdminCollapsibleSection>

      <div>
        <div className="flex flex-wrap items-baseline justify-between gap-2 mb-4">
          <h3 className="text-lg font-semibold text-[#011337]">Your solutions</h3>
          {!loading ? (
            <span className="text-sm text-slate-500">
              {groupedItems.length} item{groupedItems.length !== 1 ? "s" : ""}
            </span>
          ) : null}
        </div>
        {loading ? (
          <p className="text-sm text-slate-500 py-8 text-center rounded-xl border border-slate-200 bg-white">
            Loading solutions...
          </p>
        ) : groupedItems.length ? (
          <div className="grid gap-3">
            {groupedItems.map((group) => {
              const missing = LANGS.filter((lang) => !group.languages.includes(lang));
              return (
                <AdminProductListCard
                  key={group.key}
                  id={group.rep?.id}
                  title={group.rep?.title || group.rep?.solutionKey}
                  meta={[
                    group.rep?.solutionKey ? `Key: ${group.rep.solutionKey}` : null,
                    `Languages: ${group.languages.map((l) => l.toUpperCase()).join(", ") || "—"}`,
                    missing.length ? `Missing: ${missing.map((l) => l.toUpperCase()).join(", ")}` : null,
                  ].filter(Boolean)}
                  status={typeof group.rep?.isActive === "boolean" ? group.rep.isActive : null}
                  imageUrl={getHeroImageCandidate(group.rep) || getCardIconCandidate(group.rep)}
                  isEditing={editingKey === group.key}
                  onEdit={() => startEdit(group)}
                  onDelete={() => handleDelete(group)}
                />
              );
            })}
          </div>
        ) : (
          <p className="text-sm text-slate-500 py-8 text-center rounded-xl border border-dashed border-slate-300 bg-slate-50">
            No solutions found. Use &quot;Add new solution&quot; above.
          </p>
        )}
      </div>

      <AdminEditorModal
        open={Boolean(editingKey)}
        title={`Edit solution: ${editForm.solutionKey || editingKey || ""}`}
        subtitle="Edit every language, then save. Changes apply to the public solution page."
        onClose={closeEdit}
        footer={
          <>
            <button type="button" onClick={closeEdit} className={btnSecondary}>
              Cancel
            </button>
            <button type="button" onClick={saveEdit} disabled={saving} className={btnPrimary}>
              {saving ? "Saving..." : "Save changes"}
            </button>
          </>
        }
      >
        <div className="grid gap-4">
          {renderSharedFields(editForm, setEditForm, { isEdit: true })}
          {renderLangTabs(activeEditLang, setActiveEditLang, editForm, "edit")}
          {renderLangContent(editForm, setEditForm, activeEditLang, "edit")}

          <AdminProductPdfsPanel
            variant="solution"
            productId={editRepId}
            productSlug={String(editForm.solutionKey ?? "").trim()}
            pdfDocuments={items.find((x) => Number(x.id) === Number(editRepId))?.pdfDocuments}
            onRefresh={() => refreshSolutionKey(editForm.solutionKey)}
          />
        </div>
      </AdminEditorModal>
    </section>
  );
};

export default SolutionsManager;
